import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import type moment from 'moment'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import TooltipWrapper from 'modules/shift_management/components/ShiftManagement/TooltipWrapper'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { Shift } from 'modules/shift_management/lib/types'
import TooltipLabel from 'modules/time_planning/components/Breaks/TooltipLabel'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

import DailyShiftDetails from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/ShiftCell/BaseShift/ShiftLayout/DailyShiftDetails'
import ShiftTime from './ShiftTime'
import ShiftTitle from './ShiftTitle'

export interface Props {
  shift: Shift
  date: moment.Moment
  preview: boolean
  plannedBreaks: PlannedBreak[]
}

const ShiftDetails = ({ shift, date, preview, plannedBreaks }: Props) => {
  const [{ view, shiftsConflicts }] = useSchedule()

  const { viewOptions } = useCurrentViewOptions({ preview })
  const {
    schedule: { showName, showWorkplace, showWorkArea, showOvertime, showBreaks, showNotes },
  } = viewOptions

  const isBackup = shift.isBackup
  const hasOvertime = view.isFullVersion && shift.isOvertime
  const shortShiftInDaily = view.isDaily && shift.isAnHourOrLessLong
  const hasEmployeeName = view.isByAreas && view.isFullVersion
  const hasLocation = view.isByEmployees && view.isFullVersion && shift.location
  const hasWorkArea = view.isFullVersion && hasLocation && shift.workArea
  const hasConflictAreaView = view.isByAreas && shiftsConflicts.has(shift.id)

  const timezone = shift.employeeTimeZone || 'Utc'

  const displayName = showName && (view.isWeekly || view.isDaily)
  const displayBreaks = showBreaks && !!plannedBreaks.length && !isBackup
  const displayNotes = showNotes && !!shift.notes && !isBackup
  const displayWorkArea = showWorkArea && hasWorkArea && !isBackup
  const displayOvertime = showOvertime && hasOvertime && !isBackup
  const displayWorkplace = showWorkplace && hasLocation && !isBackup

  const displayExtras = displayBreaks || displayNotes || displayWorkArea

  return (
    <Box flexDirection="column" gap="s2" minWidth="s40">
      {displayOvertime && !shortShiftInDaily && (
        <Box padding="s2">
          <Text caps size="50" weight="bold" color="grey1000">
            {i18n.t('shift_management.overtime_tag')}
          </Text>
        </Box>
      )}
      {isBackup && !shortShiftInDaily && (
        <TooltipWrapper.Alt label={i18n.t('shift_management.backup_shifts.description')}>
          <Box padding="s2">
            <Text caps size="50" weight="bold" color="grey600">
              {i18n.t('shift_management.backup_shifts.tag')}
            </Text>
          </Box>
        </TooltipWrapper.Alt>
      )}
      {view.isDaily ? (
        <DailyShiftDetails
          shift={shift}
          templateTitle={shift.displayName}
          date={date}
          plannedBreaks={plannedBreaks}
        />
      ) : (
        <Box>
          {displayName && <ShiftTitle shift={shift} templateTitle={shift.displayName} />}
          {!isBackup && <ShiftTime shift={shift} date={date} plannedBreaks={plannedBreaks} />}
          {isBackup && !shift.name && (
            <Text.Normal size="100" weight="bold" color="grey600">
              {shift.startTime} - {shift.endTime}
            </Text.Normal>
          )}
        </Box>
      )}
      {displayWorkplace && (
        <Box overflowX="hidden">
          <Text size="100" color="grey1000" ellipsis>
            {shift.location.attributes.name}
          </Text>
        </Box>
      )}

      {displayExtras && (
        <Box flexDirection="row" gap="s4" alignItems="center" paddingBottom="s2">
          {displayBreaks && (
            <TooltipIcon.Small
              icon="coffee"
              label={
                <Box flexDirection="column" gap="s4">
                  {plannedBreaks.length > 1 && (
                    <Text size="100" color="grey000White">
                      {plannedBreaks.length} {i18n.t('company_settings.time.breaks.title')}:
                    </Text>
                  )}
                  {plannedBreaks.map((plannedBreak) => (
                    <Box key={plannedBreak.id}>
                      {plannedBreaks.length > 1 && <Divider backgroundColor="grey1000white" />}
                      <Box gap="s4">
                        <TooltipLabel plannedBreak={plannedBreak} timezone={timezone} size="100" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              }
            />
          )}
          {displayNotes && <TooltipIcon.Small icon="comment" label={shift.notes} />}
          {displayWorkArea && (
            <TooltipIcon.Small icon="space-control" label={shift.workArea.attributes.name} />
          )}
        </Box>
      )}

      {hasEmployeeName && (
        <Box overflowX="hidden">
          <Text
            size="100"
            color={hasConflictAreaView ? 'radicalRed1000' : isBackup ? 'grey600' : 'grey1000'}
            ellipsis
          >
            {shift.employeeFullName}
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default observer(ShiftDetails)
